import { lazy } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

import { RequireAnonymous, RequireAuth } from '@/app/guards'
import AuthLayout from '@/shared/layouts/AuthLayout'
import CompanyLayout from '@/shared/layouts/CompanyLayout'
import MarketingLayout from '@/shared/layouts/MarketingLayout'
import PublicLayout from '@/shared/layouts/PublicLayout'

// Code-splitting por área: un aspirante nunca descarga el panel de empresa.
const JobListPage = lazy(() => import('@/features/jobs/pages/JobListPage'))
const AccessHubPage = lazy(() => import('@/features/auth/pages/AccessHubPage'))
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'))
const CompanyRegisterPage = lazy(() => import('@/features/auth/pages/CompanyRegisterPage'))
const CandidateAccessPage = lazy(() => import('@/features/auth/pages/CandidateAccessPage'))
const CandidateLoginPage = lazy(() => import('@/features/auth/pages/CandidateLoginPage'))
const CandidateRegisterPage = lazy(() => import('@/features/auth/pages/CandidateRegisterPage'))
const ActivatePage = lazy(() => import('@/features/auth/pages/ActivatePage'))
const ForgotPasswordPage = lazy(() => import('@/features/auth/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/features/auth/pages/ResetPasswordPage'))
const DashboardPage = lazy(() => import('@/features/company/pages/DashboardPage'))
const JobCandidatesPage = lazy(() => import('@/features/company/pages/JobCandidatesPage'))
const InterviewNotesPage = lazy(() => import('@/features/company/pages/InterviewNotesPage'))
const JobPostingsPage = lazy(() => import('@/features/company/pages/JobPostingsPage'))
const CompanySubscriptionPage = lazy(() => import('@/features/company/pages/SubscriptionPage'))
const StaffAvailabilityPage = lazy(
  () => import('@/features/company/pages/StaffAvailabilityPage')
)
const JobDetailPage = lazy(() => import('@/features/jobs/pages/JobDetailPage'))
const MyApplicationsPage = lazy(() => import('@/features/apply/pages/MyApplicationsPage'))
const InterviewRoomRouter = lazy(() => import('@/features/interview/pages/InterviewRoomRouter'))
const PlansPage = lazy(() => import('@/features/subscriptions/pages/PlansPage'))
const CheckoutGatewayPage = lazy(
  () => import('@/features/subscriptions/pages/CheckoutGatewayPage')
)
const PartnersPage = lazy(() => import('@/features/jobs/pages/PartnersPage'))
const CheckoutSuccessPage = lazy(() => import('@/features/subscriptions/pages/CheckoutSuccessPage'))
const CheckoutCancelledPage = lazy(
  () => import('@/features/subscriptions/pages/CheckoutCancelledPage')
)
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))
const CompaniesLandingPage = lazy(
  () => import('@/features/marketing/pages/CompaniesLandingPage')
)
const PrivacyPolicyPage = lazy(() => import('@/features/legal/pages/PrivacyPolicyPage'))
const TermsPage = lazy(() => import('@/features/legal/pages/TermsPage'))

const STAFF_ROLES = ['OWNER', 'RECRUITER', 'VIEWER']

export const router = createBrowserRouter([
  {
    // La landing es la puerta de venta: qué es, para qué sirve y cuánto
    // cuesta, antes de pedir ningún registro. Header horizontal propio (como
    // el resto de landings de Safyro), no el sidebar de la app — nadie que
    // llega a "/" tiene sesión todavía.
    element: <MarketingLayout />,
    children: [{ path: '/', element: <CompaniesLandingPage /> }],
  },
  {
    element: <PublicLayout />,
    children: [
      {
        path: '/vacantes',
        children: [
          // La bolsa de empleo ya no vive en `/`: la lista lleva columna de
          // filtros propia y necesita todo el ancho, igual que antes.
          { index: true, element: <JobListPage />, handle: { fullWidth: true } },
          { path: ':publicId', element: <JobDetailPage /> },
        ],
      },
      { path: '/planes', element: <PlansPage />, handle: { fullWidth: true } },
      { path: '/planes/pasarela', element: <CheckoutGatewayPage /> },
      { path: '/aliados', element: <PartnersPage />, handle: { fullWidth: true } },
      // A donde redirigen las pasarelas de pago tras el checkout de
      // subscription-requests (ver STRIPE_SUCCESS_URL/_CANCEL_URL y
      // RAPYD_COMPLETE_CHECKOUT_URL/_ERROR_PAYMENT_URL en humania-api).
      // Publicas: quien llega no tiene sesion todavia.
      { path: '/checkout/exito', element: <CheckoutSuccessPage /> },
      { path: '/checkout/cancelado', element: <CheckoutCancelledPage /> },
      { path: '/legal/privacidad', element: <PrivacyPolicyPage /> },
      { path: '/legal/terminos', element: <TermsPage /> },
      {
        path: '/mis-postulaciones',
        element: (
          <RequireAuth>
            <MyApplicationsPage />
          </RequireAuth>
        ),
      },
      {
        path: '/entrevista/:interviewId',
        element: (
          <RequireAuth>
            <InterviewRoomRouter />
          </RequireAuth>
        ),
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    // Todas las pantallas de acceso (hub, login, registro, recuperar clave):
    // mismo patrón que el login de safyro-contadores/safyro-fintrack, logo
    // centrado sobre el fondo de marca, sin el sidebar de la app.
    element: <AuthLayout />,
    children: [
      {
        path: '/login',
        element: (
          <RequireAnonymous>
            <AccessHubPage />
          </RequireAnonymous>
        ),
      },
      {
        path: '/login/empresa',
        element: (
          <RequireAnonymous>
            <LoginPage />
          </RequireAnonymous>
        ),
      },
      {
        path: '/registro-empresa',
        element: (
          <RequireAnonymous>
            <CompanyRegisterPage />
          </RequireAnonymous>
        ),
      },
      // Sin RequireAnonymous: el enlace del correo tiene que poder canjearse
      // aunque el navegador conserve una sesión anterior.
      { path: '/acceso', element: <CandidateAccessPage /> },
      {
        path: '/aspirantes/ingresar',
        element: (
          <RequireAnonymous>
            <CandidateLoginPage />
          </RequireAnonymous>
        ),
      },
      {
        path: '/aspirantes/registrar',
        element: (
          <RequireAnonymous>
            <CandidateRegisterPage />
          </RequireAnonymous>
        ),
      },
      { path: '/activar', element: <ActivatePage /> },
      { path: '/recuperar-contrasena', element: <ForgotPasswordPage /> },
      { path: '/restablecer-contrasena', element: <ResetPasswordPage /> },
    ],
  },
  // La landing se movió a `/`: quien tenga el enlace viejo guardado no debe
  // quedarse en un 404.
  { path: '/empresas', element: <Navigate to="/" replace /> },
  {
    path: '/empresa',
    element: (
      <RequireAuth roles={STAFF_ROLES}>
        <CompanyLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'vacantes', element: <JobPostingsPage /> },
      { path: 'vacantes/:jobId/candidatos', element: <JobCandidatesPage /> },
      {
        path: 'vacantes/:jobId/candidatos/:applicationId/entrevista/:interviewId',
        element: <InterviewNotesPage />,
      },
      { path: 'suscripcion', element: <CompanySubscriptionPage /> },
      { path: 'mi-disponibilidad', element: <StaffAvailabilityPage /> },
      // La empresa ya existe: el pago no debe crear otra, así que reusa
      // PlansPage en modo autoservicio (`selfService`) en vez del formulario
      // público de /planes.
      { path: 'planes', element: <PlansPage selfService /> },
      { path: 'planes/pasarela', element: <CheckoutGatewayPage selfService /> },
    ],
  },
])
